import { useEffect, useRef } from "react";
import type { NavigateFunction } from "react-router-dom";
import { circadiaEngineProvider } from "../AppController";
import { useAppNavigate } from "../hooks/router";

const SESSION_KEY = "circadia_pending_notification_route";

let listenerInstalled = false;

function extractOpenRoute(payload: unknown): string | undefined {
  if (!payload || typeof payload !== "object") return undefined;
  const p = payload as { notification?: { data?: Record<string, unknown> } };
  const data = p.notification?.data;
  const raw = data?.openRoute;
  return typeof raw === "string" && raw.trim().length > 0
    ? raw.trim()
    : undefined;
}

function normalisePath(route: string): string {
  return route.startsWith("/") ? route : `/${route}`;
}

/**
 * If a notification open was recorded before the router was ready, navigate once
 * the shell is mounted (e.g. after PIN / auth).
 */
export function tryConsumePendingNotificationRoute(
  navigate: NavigateFunction
): void {
  try {
    const pending = sessionStorage.getItem(SESSION_KEY);
    if (!pending) return;
    sessionStorage.removeItem(SESSION_KEY);
    navigate(normalisePath(pending));
  } catch {
    /* ignore */
  }
}

/**
 * Registers `setNotifOpenedCb` once and routes `openRoute` from local notification
 * payloads (morning briefing / reminders) into the hash router.
 */
export function useNativeLocalNotificationDeepLink(): void {
  const navigate = useAppNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!circadiaEngineProvider.isWindowCircadia()) return;

    const engine = circadiaEngineProvider as unknown as {
      setNotifOpenedCb?: (cb: ((payload: unknown) => void) | null) => void;
    };

    if (typeof engine.setNotifOpenedCb !== "function") return;

    if (!listenerInstalled) {
      listenerInstalled = true;
      void engine.setNotifOpenedCb((payload: unknown) => {
        const route = extractOpenRoute(payload);
        if (!route) return;
        const path = normalisePath(route);
        try {
          sessionStorage.setItem(SESSION_KEY, path);
        } catch {
          /* ignore */
        }
        navigateRef.current(path);
      });
    }

    tryConsumePendingNotificationRoute(navigateRef.current);
  }, [navigate]);
}
